// ─── LIS stage of a form field ───────────────────────────────────────────────
//
// Which laboratory stage a form field belongs to is set in the form builder: on
// a field, on a table column, or overridden on a single cell. A lab result is
// mapped to a field on the Integrations → Mapping tab, and the field's stage
// decides which order it is written for.
//
// The backend resolves the same thing in `stageOfTarget` (cgt-backend
// src/integrations/lis/lis-field-stage.ts) when a result arrives; this copy only
// lets the Mapping tab SHOW it. Same search order, same cell rule.
//
// Pure on purpose: `vitest.config.ts` matches only `src/**/*.test.ts`.

import type { TableCellConfig, TableColumn, TableConfig } from '@/types/cgt'
import { allFields } from '@/lib/formSections'
import { asTableConfig, splitCellKey } from '@/lib/mappingTargets'

type DefinitionLike = Parameters<typeof allFields>[0]

function stageOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

/** Does this cell override its column's stage? The KEY is the override, so `null` counts. */
export function cellOverridesStage(cell: TableCellConfig | undefined): boolean {
  return !!cell && Object.prototype.hasOwnProperty.call(cell, 'lisStage')
}

/** A cell's stage: its own override when it has one, otherwise its column's. */
export function cellStage(config: TableConfig, rowId: string, col: TableColumn): string | null {
  const cell = config.cells?.[`${rowId}__${col.id}`]
  return cellOverridesStage(cell) ? stageOrNull(cell!.lisStage) : stageOrNull(col.lisStage)
}

/**
 * The stage of a mapping target — a field id, or a table cell key
 * `${rowId}__${colId}`. Null means no stage: the target is written for an order
 * at any stage, unless another target for the same value is set to it.
 *
 * A flat field is checked first, so a field id containing `__` is never read as
 * a cell. A cell key carries no field id, so every grid is searched: the
 * form-level one first, then each table field.
 */
export function lisStageOfTarget(
  def: DefinitionLike | null | undefined,
  targetFieldId: string,
): string | null {
  return settingOfTarget(def, targetFieldId, 'lisStage')
}

/** Does this cell override its column's test code? Same KEY rule as the stage. */
export function cellOverridesTestCode(cell: TableCellConfig | undefined): boolean {
  return !!cell && Object.prototype.hasOwnProperty.call(cell, 'lisTestCode')
}

/** A cell's test code: its own override when it has one, otherwise its column's. */
export function cellTestCode(config: TableConfig, rowId: string, col: TableColumn): string | null {
  const cell = config.cells?.[`${rowId}__${col.id}`]
  return cellOverridesTestCode(cell) ? stageOrNull(cell!.lisTestCode) : stageOrNull(col.lisTestCode)
}

/**
 * The LIS test code a target is connected to (z8nrz7g2wm), resolved like its
 * stage. Null means none. A label shown on the form, not a routing rule: which
 * value lands where is the integration's mapping.
 */
export function lisTestCodeOfTarget(
  def: DefinitionLike | null | undefined,
  targetFieldId: string,
): string | null {
  return settingOfTarget(def, targetFieldId, 'lisTestCode')
}

function settingOfTarget(
  def: DefinitionLike | null | undefined,
  targetFieldId: string,
  key: 'lisStage' | 'lisTestCode',
): string | null {
  if (!def) return null
  const fields = allFields(def)
  const flat = fields.find((f) => f.id === targetFieldId)
  if (flat) return stageOrNull(flat[key])

  const parts = splitCellKey(targetFieldId)
  if (!parts) return null
  const grids = [
    asTableConfig((def as { tableConfig?: unknown }).tableConfig),
    ...fields.map((f) => (f.fieldType === 'table' ? asTableConfig(f.tableConfig) : null)),
  ].filter((g): g is TableConfig => !!g)

  for (const config of grids) {
    const col = config.columns.find((c) => c.id === parts.colId)
    const hasRow = (config.rows ?? []).some((r) => r.id === parts.rowId)
    if (col && hasRow) {
      return key === 'lisStage' ? cellStage(config, parts.rowId, col) : cellTestCode(config, parts.rowId, col)
    }
  }
  return null
}

/**
 * Stage names a picker offers: the tenant's active stages, plus the one already
 * chosen if it is no longer among them (renamed, deactivated or deleted). Without
 * it the Select would render blank and the author could not see what is set.
 */
export function stageChoices(activeNames: string[], current: string | null | undefined): string[] {
  const chosen = stageOrNull(current)
  return chosen && !activeNames.includes(chosen) ? [...activeNames, chosen] : activeNames
}

/**
 * Test codes a picker offers for a target at `stage`: that stage's tests, or
 * every active stage's when the target has no stage, plus the one already set
 * if it is no longer among them. Each code once, in configured order.
 */
export function testCodeChoices(
  stages: ReadonlyArray<{ name: string; active: boolean; testCodes: ReadonlyArray<{ testCode: string }> }>,
  stage: string | null | undefined,
  current: string | null | undefined,
): string[] {
  const from = stage ? stages.filter((s) => s.name === stage) : stages.filter((s) => s.active)
  const codes = [...new Set(from.flatMap((s) => s.testCodes.map((t) => t.testCode)))]
  const chosen = stageOrNull(current)
  return chosen && !codes.includes(chosen) ? [...codes, chosen] : codes
}
